import type { ScoreOption } from "../api.js";

/**
 * Tap 2 — the losing score.
 *
 * FR-ENTRY-3a: one screen, both outcomes. The normal results and the legal
 * deuce results appear together under separate labels, so a deuce game costs
 * exactly the same one tap as any other and nothing has to be declared in
 * advance.
 *
 * FR-ENTRY-8: every value here came from scoring-rules. An illegal score is
 * not rejected, it is simply not on the screen.
 *
 * The target is chosen on the Play screen, before the game, so this grid is
 * already the right one when it opens. It is stated at the top of the screen
 * anyway — a result being entered against the wrong target is the one mistake
 * here that a member cannot see from the numbers alone, because 15-9 and 21-9
 * are both perfectly ordinary scores.
 */
export function ScoreGrid({
  targetScore,
  options,
  winnerNames,
  loserNames,
  busy,
  error,
  onPick,
  onCancel,
}: {
  targetScore: number;
  options: ScoreOption[];
  winnerNames: string[];
  loserNames: string[];
  busy: boolean;
  error: string | null;
  onPick: (loserScore: number, winnerScore: number) => Promise<void>;
  onCancel: () => void;
}): React.JSX.Element {
  const normal = options.filter((o) => o.group === "normal" && o.winnerScore === targetScore);
  const deuce = options.filter((o) => o.group === "deuce");
  // BR-SCORE-1 — stated rather than assumed, so the second grid's label says
  // what it is bounded by.
  const cap = deuce.reduce((highest, o) => Math.max(highest, o.winnerScore), targetScore);

  return (
    <main className="score">
      <div className="eyebrow">Tap the losing score</div>

      {/* Who this result is about, said back before it is committed to. */}
      <div className="scorehead">
        <div className="w">
          <b>{winnerNames.join(" & ")}</b> won
        </div>
        <div className="l">
          against {loserNames.join(" & ")} · playing to {targetScore}
        </div>
      </div>


      <p className="gridlbl">Won {targetScore} — tap what they got</p>
      <div className="grid">
        {normal.map((o) => (
          <button
            key={`n-${String(o.loserScore)}`}
            className="score-cell num"
            disabled={busy}
            onClick={() => {
              void onPick(o.loserScore, o.winnerScore);
            }}
          >
            {o.loserScore}
          </button>
        ))}
      </div>

      {deuce.length > 0 ? (
        <>
          <p className="gridlbl">
            Or it went past {targetScore} (cap {cap})
          </p>
          <div className="grid grid-wide">
            {deuce.map((o) => (
              <button
                key={`d-${String(o.winnerScore)}-${String(o.loserScore)}`}
                className="score-cell num"
                disabled={busy}
                onClick={() => {
                  void onPick(o.loserScore, o.winnerScore);
                }}
              >
                {o.winnerScore}–{o.loserScore}
              </button>
            ))}
          </div>
        </>
      ) : null}

      {error ? <p className="error">{error}</p> : null}

      {/* Cancel at the bottom, out of the way of the numbers, as the mockup
          has it. At the top it sat a thumb's width from the grid. */}
      <button className="btn" onClick={onCancel}>
        Cancel
      </button>
    </main>
  );
}
